import React, { useContext, useState, useMemo, useEffect } from 'react';
import { GlobalContext } from '../context/GlobalContext';
import useApi from '../hooks/useApi';
import haxios from '../lib/haxios';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, isSameDay, isSameMonth, isToday, getDay, startOfDay, endOfDay, startOfYear } from 'date-fns';
import {
  Loader2, Search, Plus, Calendar, Check, X,
  ChevronLeft, ChevronRight, Filter, User, Users,
  Trash2, Camera, ChevronDown, ChevronUp, CalendarDays, TrendingUp, AlertCircle, CheckCircle2, ListChecks, MapPin
} from 'lucide-react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Combobox } from '@headlessui/react';
import { successToast, errorToast } from '../lib/helpers';
import VisitMap from '../components/AdvancedReports/shared/VisitMap';

const localizer = momentLocalizer(moment);

function RepsVisits() {
  const { uinfo, apir } = useContext(GlobalContext);
  
  // Calendar view state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // 'month', 'week', 'day'
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Modal states
  const [showAddVisit, setShowAddVisit] = useState(false);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  
  // Filters
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    purpose: 'all'
  });
  
  // Form state
  const [visitForm, setVisitForm] = useState({
    rep_code: '',
    branch: '',
    visit_datetime: '',
    customer_account: '',
    customer_postcode: '',
    visit_purpose: '',
    notes: ''
  });
  
  const [customerSearch, setCustomerSearch] = useState('');
  const [newNote, setNewNote] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  
  // Collapsible sections
const [expandedSections, setExpandedSections] = useState({
    'visitlocations': false,
    'repswithnovisits': false,
    'mostvisits': false,
    'upcomingvisits': false,
    'completionrate': false,
    'reasonsforvisits': false
  });

  // Calculate date range based on view
  const getDateRange = () => {
    switch (view) {
      case 'month':
        return {
          start: startOfMonth(currentDate),
          end: endOfMonth(currentDate)
        };
      case 'week':
        return {
          start: startOfWeek(currentDate, { weekStartsOn: 1 }),
          end: endOfWeek(currentDate, { weekStartsOn: 1 })
        };
      case 'day':
        return {
          start: startOfDay(currentDate),
          end: endOfDay(currentDate)
        };
      default:
        return {
          start: startOfMonth(currentDate),
          end: endOfMonth(currentDate)
        };
    }
  };

  const dateRange = getDateRange();

  // Fetch visits for current date range
  const {
    data: visits,
    isLoading,
    refetch: fetchVisits
  } = useApi({
    url: `/visiondashboard/repvisits?start_date=${format(dateRange.start, 'yyyy-MM-dd')}&end_date=${format(dateRange.end, 'yyyy-MM-dd')}${showOnlyMine ? `&rep_code=${uinfo?.Inits}` : ''}`,
    initial: { data: [] },
    enabled: !!apir
  });

  // Fetch all visits for the month for calendar display
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const {
    data: allMonthVisits,
    refetch: fetchAllMonthVisits
  } = useApi({
    url: `/visiondashboard/repvisits?start_date=${format(monthStart, 'yyyy-MM-dd')}&end_date=${format(monthEnd, 'yyyy-MM-dd')}`,
    initial: { data: [] },
    enabled: !!apir
  });

  // Fetch all visits for the year (year-to-date) for KPI calculations and management tables
  const yearStart = startOfYear(new Date());
  const today = new Date();
  const {
    data: allYearVisits,
    refetch: fetchAllYearVisits
  } = useApi({
    url: `/visiondashboard/repvisits?start_date=${format(yearStart, 'yyyy-MM-dd')}&end_date=${format(today, 'yyyy-MM-dd')}`,
    initial: { data: [] },
    enabled: !!apir
  });

  // Use the same endpoint as BDM Activity report to get all operators/reps
  const { data: reps } = useApi({
    url: '/salesassist/cplusassist/getOperators',
    initial: [],
    enabled: (showAddVisit || true) && !!apir
  });

  const { data: branchList } = useApi({
    url: '/visiondashboard/targets/branches',
    initial: [],
    enabled: (showAddVisit && visitForm.rep_code) || true && !!apir
  });

  const { data: customers } = useApi({
    url: `/visiondashboard/customers${visitForm.branch ? `?branch=${visitForm.branch.MamCode}` : ''}`,
    initial: [],
    enabled: showAddVisit && visitForm.rep_code && visitForm.branch && !!apir
  });
  const minSearchLength = 2;

  const customerList = useMemo(() => {
    if (Array.isArray(customers)) return customers;
    return customers?.data || [];
  }, [customers]);
  const filteredCustomers = useMemo(() => {
    const search = customerSearch.toLowerCase();
    if (!search) return customerList;
    return customerList.filter(c => {
      const keyCode = c?.KeyCode?.toLowerCase() || '';
      const name = c?.Name?.toLowerCase() || '';
      return keyCode.includes(search) || name.includes(search);
    });
  }, [customerList, customerSearch]);
  const displayedCustomers = useMemo(() => {
    if (customerSearch.trim().length < minSearchLength) return [];
    return filteredCustomers.slice(0, 50);
  }, [customerSearch, filteredCustomers, minSearchLength]);

  // Set default rep if user is a rep
  useEffect(() => {
    if (uinfo?.Inits && reps?.some(rep => rep.Inits === uinfo.Inits)) {
      setVisitForm(prev => ({
        ...prev,
        rep_code: uinfo.Inits
      }));
    }
  }, [uinfo, reps]);

  // Convert visits to calendar events
  const calendarEvents = useMemo(() => {
    if (!visits?.data) return [];
    
    return visits.data.map(visit => ({
      id: visit.visit_id,
      title: `${visit.customer_account} - ${visit.rep_code}`,
      start: new Date(visit.visit_datetime),
      end: new Date(new Date(visit.visit_datetime).getTime() + 60 * 60 * 1000), // 1 hour duration
      resource: visit
    }));
  }, [visits]);

  // Organize visits by date for the map component
  const organizedVisitsForMap = useMemo(() => {
    if (!allMonthVisits?.data) return new Map();
    
    const organized = new Map();
    allMonthVisits.data.forEach(visit => {
      // Only include visits with postcodes
      if (!visit.customer_postcode) return;
      
      const visitDate = new Date(visit.visit_datetime);
      const dateKey = format(visitDate, 'yyyy-MM-dd');
      
      if (!organized.has(dateKey)) {
        organized.set(dateKey, []);
      }
      organized.get(dateKey).push(visit);
    });
    
    return organized;
  }, [allMonthVisits]);

  // Calculate KPIs - using year-to-date data for cards and management tables
  const kpis = useMemo(() => {
    if (!allYearVisits?.data) {
      return {
        totalVisits: 0,
        completedVisits: 0,
        pendingVisits: 0,
        avgVisitsPerRep: 0,
        repsWithNoVisits: [],
        repsWithMostVisits: [],
        upcomingVisits: [],
        completionRates: [],
        reasonsForVisits: []
      };
    }

    const visitsData = allYearVisits.data;
    const totalVisits = visitsData.length;
    
    // Count completed and pending visits - handle different status formats
    const completedVisits = visitsData.filter(v => {
      const status = (v.status || '').toLowerCase();
      return status === 'completed';
    }).length;
    
    const pendingVisits = visitsData.filter(v => {
      const status = (v.status || '').toLowerCase();
      // Pending includes: null, undefined, empty string, 'pending', or anything that's not 'completed' or 'cancelled'
      return !status || status === 'pending' || (status !== 'completed' && status !== 'cancelled');
    }).length;
    
    // Group by rep - normalize rep codes to match operator codes
    const visitsByRep = visitsData.reduce((acc, visit) => {
      const visitRepCode = visit.rep_code || 'Unknown';
      // Try to find matching operator to normalize the rep code
      const matchingRep = reps?.find(r => {
        const operatorCode = r.Inits || r.inits || r.Name || r.name || r.OperatorName;
        return operatorCode && (
          operatorCode === visitRepCode ||
          operatorCode.toLowerCase() === visitRepCode.toLowerCase()
        );
      });
      // Use the normalized operator code if found, otherwise use the visit rep code
      const repCode = matchingRep ? (matchingRep.Inits || matchingRep.inits || matchingRep.Name || matchingRep.name || matchingRep.OperatorName) : visitRepCode;
      
      if (!acc[repCode]) {
        acc[repCode] = [];
      }
      acc[repCode].push(visit);
      return acc;
    }, {});

    // Calculate average visits per rep - use total number of reps, not just those with visits
    // Also get all rep codes for "reps with no visits" calculation
    // Handle different property names (Inits, inits, Name, name, etc.)
    const allRepCodes = reps?.map(r => r.Inits || r.inits || r.Name || r.name || r.OperatorName).filter(Boolean) || [];
    const totalReps = allRepCodes.length;
    const avgVisitsPerRep = totalReps > 0 ? totalVisits / totalReps : 0;

    // Reps with no visits - check all reps from the operators endpoint
    const repsWithNoVisits = allRepCodes
      .filter(repCode => {
        // Check if this rep has any visits in the year-to-date data
        return !visitsByRep[repCode] || visitsByRep[repCode].length === 0;
      })
      .map(repCode => {
        const rep = reps.find(r => 
          (r.Inits || r.inits || r.Name || r.name || r.OperatorName) === repCode
        );
        return {
          rep_code: repCode,
          name: rep?.Name || rep?.name || rep?.OperatorName || repCode,
          visits: 0
        };
      });

    // Reps with most visits
    const repsWithMostVisits = Object.entries(visitsByRep)
      .map(([repCode, repVisits]) => {
        const rep = reps.find(r => 
          (r.Inits || r.inits || r.Name || r.name || r.OperatorName) === repCode
        );
        return {
          rep_code: repCode,
          name: rep?.Name || rep?.name || rep?.OperatorName || repCode,
          visits: repVisits.length,
          completed: repVisits.filter(v => (v.status || '').toLowerCase() === 'completed').length,
          pending: repVisits.filter(v => {
            const status = (v.status || '').toLowerCase();
            return !status || status === 'pending' || (status !== 'completed' && status !== 'cancelled');
          }).length
        };
      })
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 10);

    // Upcoming visits - show all future visits in the current month that aren't completed or cancelled
    const today = startOfDay(new Date()); // Start of today for accurate comparison
    const monthEnd = endOfMonth(currentDate);
    const currentMonthVisits = allMonthVisits?.data || [];
    const upcomingVisits = currentMonthVisits
      .filter(visit => {
        const visitDate = new Date(visit.visit_datetime);
        const visitDateStart = startOfDay(visitDate);
        // Show visits that are today or in the future, within the current month, and not completed/cancelled
        const status = (visit.status || '').toLowerCase();
        return visitDateStart >= today && 
               visitDateStart <= monthEnd && 
               status !== 'completed' && 
               status !== 'cancelled';
      })
      .sort((a, b) => new Date(a.visit_datetime) - new Date(b.visit_datetime))
      .slice(0, 50); // Increased limit to show more upcoming visits

    // Completion rates by rep
    const completionRates = Object.entries(visitsByRep)
      .map(([repCode, repVisits]) => {
        const rep = reps.find(r => 
          (r.Inits || r.inits || r.Name || r.name || r.OperatorName) === repCode
        );
        const completed = repVisits.filter(v => (v.status || '').toLowerCase() === 'completed').length;
        const rate = repVisits.length > 0 ? (completed / repVisits.length) * 100 : 0;
        return {
          rep_code: repCode,
          name: rep?.Name || rep?.name || rep?.OperatorName || repCode,
          total: repVisits.length,
          completed,
          pending: repVisits.length - completed,
          rate: Math.round(rate)
        };
      })
      .sort((a, b) => b.rate - a.rate);

    // Reasons for visits - count by purpose
    const reasonsForVisits = {};
    visitsData.forEach(visit => {
      const purpose = visit.visit_purpose || 'Not specified';
      if (!reasonsForVisits[purpose]) {
        reasonsForVisits[purpose] = {
          purpose,
          total: 0,
          completed: 0,
          pending: 0,
          cancelled: 0
        };
      }
      reasonsForVisits[purpose].total++;
      const status = (visit.status || '').toLowerCase();
      if (status === 'completed') {
        reasonsForVisits[purpose].completed++;
      } else if (status === 'cancelled') {
        reasonsForVisits[purpose].cancelled++;
      } else {
        reasonsForVisits[purpose].pending++;
      }
    });

    const reasonsForVisitsArray = Object.values(reasonsForVisits)
      .sort((a, b) => b.total - a.total);

    return {
      totalVisits,
      completedVisits,
      pendingVisits,
      avgVisitsPerRep: Math.round(avgVisitsPerRep * 10) / 10,
      repsWithNoVisits,
      repsWithMostVisits,
      upcomingVisits,
      completionRates,
      reasonsForVisits: reasonsForVisitsArray
    };
  }, [allYearVisits, allMonthVisits, reps]);

  // Handle calendar navigation
  const handleNavigate = (date) => {
    setCurrentDate(date);
  };

  // Handle view change
  const handleViewChange = (newView) => {
    setView(newView);
  };

  // Handle date selection (click on day)
  const handleSelectSlot = ({ start }) => {
    setSelectedDate(start);
    setVisitForm(prev => ({
      ...prev,
      visit_datetime: format(start, "yyyy-MM-dd'T'HH:mm")
    }));
    setShowAddVisit(true);
  };

  // Handle event click
  const handleSelectEvent = (event) => {
    setSelectedVisit(event.resource);
    setShowVisitModal(true);
  };

  // Create visit
  const handleCreateVisit = async () => {
    try {
      const visitData = {
        rep_code: visitForm.rep_code,
        visit_datetime: visitForm.visit_datetime,
        customer_account: visitForm.customer_account,
        customer_postcode: visitForm.customer_postcode,
        visit_purpose: visitForm.visit_purpose,
        notes: visitForm.notes
      };

      const response = await haxios.post('/visiondashboard/repvisits', visitData);

      if (response && response.success === true && response.visit_id) {
        setVisitForm({
          rep_code: '',
          branch: '',
          visit_datetime: '',
          customer_account: '',
          customer_postcode: '',
          visit_purpose: '',
          notes: ''
        });
        setShowAddVisit(false);
        setSelectedDate(null);
        await fetchVisits();
        await fetchAllMonthVisits();
        await fetchAllYearVisits();
        successToast('Visit created successfully');
      } else {
        throw new Error('Invalid response structure from server');
      }
    } catch (error) {
      console.error('Failed to create visit:', error);
      errorToast(`Failed to create visit: ${error.message}`);
    }
  };

  // Update visit status
  const handleUpdateStatus = async (visitId, newStatus) => {
    try {
      await haxios.patch(`/visiondashboard/repvisits/${visitId}/status`, {
        status: newStatus
      });
      await fetchVisits();
      await fetchAllMonthVisits();
      await fetchAllYearVisits();
      successToast('Visit status updated');
    } catch (error) {
      console.error('Failed to update status:', error);
      errorToast('Failed to update status. Please try again.');
    }
  };

  // Toggle collapsible section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const purposeColors = {
    'Business decline': 'bg-red-100 text-red-800',
    'Product focus': 'bg-blue-100 text-blue-800',
    'Service visit': 'bg-green-100 text-green-800',
    'GE lead': 'bg-purple-100 text-purple-800',
    'Account enquiry': 'bg-yellow-100 text-yellow-800',
    'Promotional update': 'bg-orange-100 text-orange-800'
  };

  // Render add visit form modal
  const renderAddVisitModal = () => {
    if (!showAddVisit) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Schedule New Visit</h2>
              <button
                onClick={() => {
                  setShowAddVisit(false);
                  setSelectedDate(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rep</label>
                <select
                  value={uinfo.Inits && reps?.some(rep => 
                    (rep.Inits || rep.inits || rep.Name || rep.name) === uinfo.Inits
                  )
                    ? uinfo.Inits
                    : visitForm.rep_code}
                  onChange={(e) => {
                    setVisitForm(prev => ({
                      ...prev,
                      rep_code: e.target.value,
                      branch: '',
                      customer_account: '',
                      customer_postcode: ''
                    }));
                  }}
                  disabled={uinfo.Inits && reps?.some(rep => 
                    (rep.Inits || rep.inits || rep.Name || rep.name) === uinfo.Inits
                  )}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Rep...</option>
                  {reps?.map(rep => {
                    const repInits = rep.Inits || rep.inits || rep.Name || rep.name || '';
                    const repName = rep.Name || rep.name || rep.OperatorName || repInits;
                    return (
                      <option key={repInits} value={repInits}>
                        {repName} ({repInits})
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <select
                  value={visitForm.branch?.Branch || ''}
                  onChange={(e) => {
                    const selectedBranch = branchList.find(b => b.Branch === e.target.value);
                    setVisitForm(prev => ({
                      ...prev,
                      branch: selectedBranch,
                      customer_account: '',
                      customer_postcode: ''
                    }));
                  }}
                  disabled={!visitForm.rep_code}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Select Branch...</option>
                  {branchList?.map(branch => (
                    <option key={branch.Branch} value={branch.Branch}>
                      {branch.Branch}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                <Combobox
                  value={visitForm.customer_account}
                  onChange={(value) => {
                    const customer = customerList.find(c => c.KeyCode === value);
                    setVisitForm(prev => ({
                      ...prev,
                      customer_account: value,
                      customer_postcode: customer?.PCode || ''
                    }));
                  }}
                  disabled={!visitForm.branch}
                >
                  <div className="relative">
                    <Combobox.Input
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      displayValue={(value) => {
                        const selected = customerList.find(c => c.KeyCode === value);
                        return selected ? `${selected.KeyCode} - ${selected.Name}` : '';
                      }}
                      placeholder="Search Customer..."
                    />
                    <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border bg-white py-1 text-sm shadow-lg">
                      {customerSearch.trim().length < minSearchLength && (
                        <div className="px-4 py-2 text-gray-500">Type at least {minSearchLength} characters to search</div>
                      )}
                      {customerSearch.trim().length >= minSearchLength && displayedCustomers.length === 0 && (
                        <div className="px-4 py-2 text-gray-500">No customers found</div>
                      )}
                      {displayedCustomers.map(customer => (
                        <Combobox.Option
                          key={customer.KeyCode}
                          value={customer.KeyCode}
                          className={({ active }) =>
                            `cursor-pointer select-none px-4 py-2 ${active ? 'bg-blue-500 text-white' : ''}`
                          }
                        >
                          {customer.KeyCode} - {customer.Name}
                        </Combobox.Option>
                      ))}
                    </Combobox.Options>
                  </div>
                </Combobox>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
                <input
                  type="datetime-local"
                  value={visitForm.visit_datetime}
                  onChange={(e) => setVisitForm(prev => ({ ...prev, visit_datetime: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                <select
                  value={visitForm.visit_purpose}
                  onChange={(e) => setVisitForm(prev => ({ ...prev, visit_purpose: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Purpose...</option>
                  <option value="Business decline">Business decline</option>
                  <option value="Product focus">Product focus</option>
                  <option value="Service visit">Service visit</option>
                  <option value="GE lead">GE lead</option>
                  <option value="Account enquiry">Account enquiry</option>
                  <option value="Promotional update">Promotional update</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={visitForm.notes}
                  onChange={(e) => setVisitForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any additional notes..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddVisit(false);
                  setSelectedDate(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateVisit}
                disabled={!visitForm.rep_code || !visitForm.visit_datetime || !visitForm.customer_account}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Visit
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render visit details modal
  const renderVisitModal = () => {
    if (!showVisitModal || !selectedVisit) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Visit Details</h2>
              <button
                onClick={() => {
                  setShowVisitModal(false);
                  setSelectedVisit(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rep</label>
                  <p className="text-gray-900">{selectedVisit.rep_code}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                  <p className="text-gray-900">{selectedVisit.customer_account}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
                  <p className="text-gray-900">{format(new Date(selectedVisit.visit_datetime), 'PPpp')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <span className={`inline-block px-2 py-1 rounded text-sm ${
                    selectedVisit.status === 'completed' ? 'bg-green-100 text-green-800' :
                    selectedVisit.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedVisit.status || 'Pending'}
                  </span>
                </div>
                {selectedVisit.visit_purpose && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                    <span className={`inline-block px-2 py-1 rounded text-sm ${purposeColors[selectedVisit.visit_purpose] || 'bg-gray-100 text-gray-800'}`}>
                      {selectedVisit.visit_purpose}
                    </span>
                  </div>
                )}
              </div>

              {selectedVisit.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <p className="text-gray-900">{selectedVisit.notes}</p>
                </div>
              )}

              {selectedVisit.status !== 'completed' && selectedVisit.status !== 'cancelled' && (
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      handleUpdateStatus(selectedVisit.visit_id, 'completed');
                      setShowVisitModal(false);
                      setSelectedVisit(null);
                    }}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Mark as Completed
                  </button>
                  <button
                    onClick={() => {
                      handleUpdateStatus(selectedVisit.visit_id, 'cancelled');
                      setShowVisitModal(false);
                      setSelectedVisit(null);
                    }}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Cancel Visit
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render collapsible management card
  const renderManagementCard = (title, Icon, color, data, columns, renderRow) => {
    const sectionKey = title.toLowerCase().replace(/\s+/g, '');
    const isExpanded = expandedSections[sectionKey] || false;

    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4">
        <div
          className="p-4 border-b bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
              <Icon className={`h-5 w-5 ${color}`} />
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <span className="text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">
                {data.length} {data.length === 1 ? 'record' : 'records'}
              </span>
            </div>
          </div>
        </div>
        {isExpanded && (
          <div className="p-4">
            {data.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No data available</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {columns.map((col, idx) => (
                        <th key={idx} className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((item, idx) => renderRow(item, idx))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Set document title
  useEffect(() => {
    document.title = 'BDM Manager - Sales Assist';
    return () => {
      document.title = 'Sales Assist';
    };
  }, []);

  return (
    <>
      {/* Custom Calendar Styles */}
      <style>{`
        /* Calendar Container */
        .rbc-calendar {
          font-family: inherit;
        }
        
        /* Header Styling */
        .rbc-header {
          padding: 12px 8px;
          font-weight: 600;
          color: #374151;
          background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
          border-bottom: 2px solid #e5e7eb;
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.05em;
        }
        
        /* Today's date highlight */
        .rbc-today {
          background-color: #eff6ff !important;
          border-left: 3px solid #3b82f6;
        }
        
        /* Date cells */
        .rbc-date-cell {
          text-align: right;
          padding: 4px 8px;
        }
        
        .rbc-date-cell > a {
          color: #374151;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 6px;
          transition: all 0.2s;
        }
        
        .rbc-date-cell > a:hover {
          background-color: #e0e7ff;
          color: #4f46e5;
        }
        
        .rbc-off-range-bg {
          background-color: #f9fafb;
        }
        
        .rbc-off-range {
          color: #9ca3af;
        }
        
        /* Event styling */
        .rbc-event {
          border-radius: 6px;
          padding: 2px 6px;
          font-size: 0.75rem;
          font-weight: 500;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.2s;
        }
        
        .rbc-event:hover {
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          transform: translateY(-1px);
        }
        
        .rbc-event-label {
          font-weight: 600;
        }
        
        /* Time slots */
        .rbc-time-slot {
          border-top: 1px solid #e5e7eb;
        }
        
        .rbc-time-header-content {
          border-left: 1px solid #e5e7eb;
        }
        
        .rbc-time-content {
          border-top: 2px solid #e5e7eb;
        }
        
        /* Month view day cells */
        .rbc-day-bg {
          border: 1px solid #e5e7eb;
          transition: background-color 0.2s;
        }
        
        .rbc-day-bg:hover {
          background-color: #f9fafb;
        }
        
        /* Selected slot */
        .rbc-selected {
          background-color: #dbeafe !important;
        }
        
        /* Toolbar styling */
        .rbc-toolbar {
          margin-bottom: 0;
        }
        
        .rbc-toolbar button {
          color: #374151;
          border: 1px solid #d1d5db;
          background: white;
          padding: 6px 12px;
          border-radius: 6px;
          font-weight: 500;
          transition: all 0.2s;
        }
        
        .rbc-toolbar button:hover {
          background-color: #f3f4f6;
          border-color: #9ca3af;
        }
        
        .rbc-toolbar button:active,
        .rbc-toolbar button.rbc-active {
          background-color: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        
        /* Week view time labels */
        .rbc-time-header-gutter {
          background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
          border-right: 2px solid #e5e7eb;
        }
        
        .rbc-label {
          padding: 8px;
          font-weight: 500;
          color: #6b7280;
        }
        
        /* Current time indicator */
        .rbc-current-time-indicator {
          background-color: #ef4444;
          height: 2px;
        }
        
        /* Agenda view */
        .rbc-agenda-view table {
          border: 1px solid #e5e7eb;
        }
        
        .rbc-agenda-date-cell,
        .rbc-agenda-time-cell {
          padding: 12px;
          border-right: 1px solid #e5e7eb;
          background-color: #f9fafb;
        }
        
        .rbc-agenda-event-cell {
          padding: 12px;
        }
        
        /* Show more link */
        .rbc-show-more {
          background-color: #f3f4f6;
          color: #4f46e5;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 4px;
          transition: all 0.2s;
        }
        
        .rbc-show-more:hover {
          background-color: #e0e7ff;
          color: #4338ca;
        }
        
        /* Month view event container */
        .rbc-event-content {
          font-size: 0.75rem;
          line-height: 1.4;
        }
        
        /* Add some spacing and polish */
        .rbc-month-view {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .rbc-time-view {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }
      `}</style>
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">BDM Manager</h1>
              <p className="text-sm text-gray-600 mt-1">BDM Visit Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
              <button
                onClick={() => setShowOnlyMine(!showOnlyMine)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                  showOnlyMine
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white border-gray-200 text-gray-700'
                } hover:bg-blue-50 transition-colors`}
              >
                {showOnlyMine ? <User className="h-5 w-5" /> : <Users className="h-5 w-5" />}
                {showOnlyMine ? 'My Visits' : 'All Visits'}
              </button>
              <button
                onClick={() => {
                  setSelectedDate(new Date());
                  setVisitForm(prev => ({
                    ...prev,
                    visit_datetime: format(new Date(), "yyyy-MM-dd'T'HH:mm")
                  }));
                  setShowAddVisit(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="h-5 w-5" />
                Add Visit
              </button>
            </div>
          </div>
        </div>

      {/* Main Content */}
      <div className="py-6">
        <div className="px-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
            <div className="text-sm font-medium text-gray-600 mb-1">Total Visits</div>
            <div className="text-2xl font-bold text-gray-900">{kpis.totalVisits.toLocaleString('en-GB')}</div>
            <div className="text-xs text-gray-500 mt-1">Year to Date</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
            <div className="text-sm font-medium text-gray-600 mb-1">Completed</div>
            <div className="text-2xl font-bold text-gray-900">{(kpis.completedVisits || 0).toLocaleString('en-GB')}</div>
            <div className="text-xs text-gray-500 mt-1">
              {kpis.totalVisits > 0 ? Math.round((kpis.completedVisits / kpis.totalVisits) * 100) : 0}% completion
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
            <div className="text-sm font-medium text-gray-600 mb-1">Pending</div>
            <div className="text-2xl font-bold text-gray-900">{(kpis.pendingVisits || 0).toLocaleString('en-GB')}</div>
            <div className="text-xs text-gray-500 mt-1">Scheduled visits</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
            <div className="text-sm font-medium text-gray-600 mb-1">Avg per Rep</div>
            <div className="text-2xl font-bold text-gray-900">{kpis.avgVisitsPerRep}</div>
            <div className="text-xs text-gray-500 mt-1">Year to Date</div>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (view === 'month') setCurrentDate(subMonths(currentDate, 1));
                  else if (view === 'week') setCurrentDate(subWeeks(currentDate, 1));
                  else setCurrentDate(subDays(currentDate, 1));
                }}
                className="p-2 hover:bg-blue-50 rounded-lg transition-colors border border-gray-200 hover:border-blue-300"
              >
                <ChevronLeft className="h-5 w-5 text-gray-700" />
              </button>
              <h2 className="text-xl font-semibold text-gray-900">
                {view === 'month' && format(currentDate, 'MMMM yyyy')}
                {view === 'week' && `Week of ${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d, yyyy')}`}
                {view === 'day' && format(currentDate, 'EEEE, MMMM d, yyyy')}
              </h2>
              <button
                onClick={() => {
                  if (view === 'month') setCurrentDate(addMonths(currentDate, 1));
                  else if (view === 'week') setCurrentDate(addWeeks(currentDate, 1));
                  else setCurrentDate(addDays(currentDate, 1));
                }}
                className="p-2 hover:bg-blue-50 rounded-lg transition-colors border border-gray-200 hover:border-blue-300"
              >
                <ChevronRight className="h-5 w-5 text-gray-700" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setView('month')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  view === 'month' 
                    ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setView('week')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  view === 'week' 
                    ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setView('day')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  view === 'day' 
                    ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                Day
              </button>
            </div>
          </div>
          <div style={{ height: '600px' }} className="rounded-lg overflow-hidden border border-gray-200">
            <BigCalendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              view={view}
              onView={handleViewChange}
              date={currentDate}
              onNavigate={handleNavigate}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              selectable
              style={{ height: '100%' }}
              eventPropGetter={(event) => {
                const status = event.resource?.status;
                return {
                  style: {
                    backgroundColor: status === 'completed' ? '#10b981' : status === 'cancelled' ? '#ef4444' : '#3b82f6',
                    borderColor: status === 'completed' ? '#059669' : status === 'cancelled' ? '#dc2626' : '#2563eb',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontWeight: '500',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }
                };
              }}
            />
          </div>
        </div>

        {/* Visit Locations Map */}
        {organizedVisitsForMap.size > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
            <div
              className="p-4 border-b bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => toggleSection('visitlocations')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {expandedSections['visitlocations'] ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Visit Locations</h3>
                  <span className="text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">
                    {Array.from(organizedVisitsForMap.values()).flat().length} {Array.from(organizedVisitsForMap.values()).flat().length === 1 ? 'visit' : 'visits'} with locations
                  </span>
                </div>
              </div>
            </div>
            {expandedSections['visitlocations'] && (
              <div className="p-4">
                <VisitMap organizedVisits={organizedVisitsForMap} />
              </div>
            )}
          </div>
        )}

        {/* Management Cards */}
        <div className="space-y-4">
          {renderManagementCard(
            'Reps with No Visits',
            AlertCircle,
            'text-red-600',
            kpis.repsWithNoVisits,
            ['Rep Code', 'Name', 'Visits'],
            (item, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{item.rep_code}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{item.visits}</td>
              </tr>
            )
          )}

          {renderManagementCard(
            'Most Visits',
            TrendingUp,
            'text-blue-600',
            kpis.repsWithMostVisits,
            ['Rep Code', 'Name', 'Total', 'Completed', 'Pending'],
            (item, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{item.rep_code}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                <td className="px-4 py-3 text-sm font-semibold text-gray-900">{item.visits}</td>
                <td className="px-4 py-3 text-sm text-green-600">{item.completed}</td>
                <td className="px-4 py-3 text-sm text-orange-600">{item.pending}</td>
              </tr>
            )
          )}

          {renderManagementCard(
            'Upcoming Visits',
            CalendarDays,
            'text-purple-600',
            kpis.upcomingVisits,
            ['Date', 'Rep', 'Customer', 'Purpose', 'Status'],
            (item, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">
                  {format(new Date(item.visit_datetime), 'MMM d, yyyy HH:mm')}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">{item.rep_code}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{item.customer_account}</td>
                <td className="px-4 py-3 text-sm">
                  {item.visit_purpose && (
                    <span className={`px-2 py-1 rounded text-xs ${purposeColors[item.visit_purpose] || 'bg-gray-100 text-gray-800'}`}>
                      {item.visit_purpose}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={`px-2 py-1 rounded text-xs ${
                    item.status === 'completed' ? 'bg-green-100 text-green-800' :
                    item.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {item.status || 'Pending'}
                  </span>
                </td>
              </tr>
            )
          )}

          {renderManagementCard(
            'Completion Rate',
            CheckCircle2,
            'text-green-600',
            kpis.completionRates,
            ['Rep Code', 'Name', 'Total', 'Completed', 'Pending', 'Rate %'],
            (item, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{item.rep_code}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{item.total}</td>
                <td className="px-4 py-3 text-sm text-green-600">{item.completed}</td>
                <td className="px-4 py-3 text-sm text-orange-600">{item.pending}</td>
                <td className="px-4 py-3 text-sm">
                  <span className={`font-semibold ${
                    item.rate >= 80 ? 'text-green-600' :
                    item.rate >= 50 ? 'text-orange-600' :
                    'text-red-600'
                  }`}>
                    {item.rate}%
                  </span>
                </td>
              </tr>
            )
          )}

          {renderManagementCard(
            'Reasons for Visits',
            ListChecks,
            'text-indigo-600',
            kpis.reasonsForVisits,
            ['Purpose', 'Total', 'Completed', 'Pending', 'Cancelled', '% of Total'],
            (item, idx) => {
              const percentage = kpis.totalVisits > 0 ? Math.round((item.total / kpis.totalVisits) * 100) : 0;
              return (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      purposeColors[item.purpose] || 'bg-gray-100 text-gray-800'
                    }`}>
                      {item.purpose}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{item.total}</td>
                  <td className="px-4 py-3 text-sm text-green-600">{item.completed}</td>
                  <td className="px-4 py-3 text-sm text-orange-600">{item.pending}</td>
                  <td className="px-4 py-3 text-sm text-red-600">{item.cancelled}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="font-medium text-gray-700">{percentage}%</span>
                  </td>
                </tr>
              );
            }
          )}
        </div>
        </div>
      </div>

      {/* Modals */}
      {renderAddVisitModal()}
      {renderVisitModal()}
    </div>
    </>
  );
}

export default RepsVisits;
